import ApiService from './api';
import api from '../hooks/useHttp';

class PaymentService extends ApiService {
  constructor() {
    super('/payments');
  }

  // ==================== PaymentReceivable (From Customers) ====================

  // Get all receivable payments with pagination and filters
  async getReceivables(params = {}) {
    return await this.get('/receivable', params);
  }

  // Get receivable payment by ID
  async getReceivableById(id) {
    return await this.get(`/receivable/${id}`);
  }

  // Get receivable statistics
  async getReceivableStats() {
    return await this.get('/receivable/stats');
  }

  // Get overdue receivables
  async getOverdueReceivables(params = {}) {
    return await this.get('/receivable/overdue', params);
  }

  // Get receivables by status
  async getReceivablesByStatus(status, params = {}) {
    return await this.get(`/receivable/status/${status}`, params);
  }

  // Get receivables by customer
  async getReceivablesByCustomer(customerId, params = {}) {
    return await this.get(`/receivable/customer/${customerId}`, params);
  }

  // Get receivable by load
  async getReceivableByLoad(loadId) {
    return await this.get(`/receivable/load/${loadId}`);
  }

  // Create new receivable
  async createReceivable(data) {
    return await this.create(data, '/receivable');
  }

  // Update receivable
  async updateReceivable(id, data) {
    return await this.update(id, data, '/receivable');
  }

  // Update receivable with files (FormData)
  async updateReceivableWithFiles(id, formData) {
    return await api.put(`${this.baseUrl}/receivable/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // Create invoice for receivable
  async createInvoice(id, dueDate = null) {
    return await api.patch(`${this.baseUrl}/receivable/${id}/invoice`, { dueDate });
  }

  // Record payment for receivable
  async recordReceivablePayment(id, paymentData) {
    return await api.patch(`${this.baseUrl}/receivable/${id}/payment`, paymentData);
  }

  // Delete receivable (admin only)
  async deleteReceivable(id) {
    return await this.delete(id, '/receivable');
  }

  // Search receivables
  async searchReceivables(query, filters = {}) {
    return await this.get('/receivable', { search: query, ...filters });
  }

  // ==================== PaymentPayable (To Carriers) ====================

  // Get all payable payments with pagination and filters
  async getPayables(params = {}) {
    return await this.get('/payable', params);
  }

  // Get payable payment by ID
  async getPayableById(id) {
    return await this.get(`/payable/${id}`);
  }

  // Get payable statistics
  async getPayableStats() {
    return await this.get('/payable/stats');
  }

  // Get payables by status
  async getPayablesByStatus(status, params = {}) {
    return await this.get(`/payable/status/${status}`, params);
  }

  // Get payables by carrier
  async getPayablesByCarrier(carrierId, params = {}) {
    return await this.get(`/payable/carrier/${carrierId}`, params);
  }

  // Get payable by load
  async getPayableByLoad(loadId) {
    return await this.get(`/payable/load/${loadId}`);
  }

  // Create new payable
  async createPayable(data) {
    return await this.create(data, '/payable');
  }

  // Update payable
  async updatePayable(id, data) {
    return await this.update(id, data, '/payable');
  }

  // Update payable with files (FormData)
  async updatePayableWithFiles(id, formData) {
    return await api.put(`${this.baseUrl}/payable/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // Schedule payment for payable
  async schedulePayment(id, scheduleData) {
    return await api.patch(`${this.baseUrl}/payable/${id}/schedule`, scheduleData);
  }

  // Record payment for payable
  async recordPayablePayment(id, paymentData) {
    return await api.patch(`${this.baseUrl}/payable/${id}/payment`, paymentData);
  }

  // Update deductions for payable
  async updateDeductions(id, deductionsData) {
    return await api.patch(`${this.baseUrl}/payable/${id}/deductions`, deductionsData);
  }

  // Delete payable (admin only)
  async deletePayable(id) {
    return await this.delete(id, '/payable');
  }

  // Search payables
  async searchPayables(query, filters = {}) {
    return await this.get('/payable', { search: query, ...filters });
  }

  // ==================== Legacy methods (for backward compatibility) ====================

  // Get all payments (generic - redirects to receivables by default)
  async getPayments(params = {}) {
    return await this.getReceivables(params);
  }

  // Get payment by ID
  async getPaymentById(id) {
    return await this.get(`/${id}`);
  }

  // Create new payment
  async createPayment(paymentData) {
    return await this.create(paymentData);
  }

  // Update payment
  async updatePayment(id, paymentData) {
    return await this.partialUpdate(id, paymentData);
  }

  // Delete payment
  async deletePayment(id) {
    return await this.delete(id);
  }

  // Get payment statistics (combined)
  async getPaymentStats() {
    return await this.get('/stats');
  }

  // Search payments
  async searchPayments(query, filters = {}) {
    return await this.get('/search', { q: query, ...filters });
  }

  // Update payment status
  async updatePaymentStatus(id, status) {
    return await this.partialUpdate(id, { status }, '/status');
  }
}

export default new PaymentService();
